import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { ArrowLeft, CalendarClock, Plus, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (d) =>
  new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

const durationLabel = (start, end) => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = eh * 60 + em - (sh * 60 + sm);
  return `${Math.floor(mins / 60)}h ${mins % 60 ? `${mins % 60}m` : ''}`.trim();
};

const TeacherAvailabilityManagement = ({ teacherIdOverride }) => {
  const params = useParams();
  const teacherId = teacherIdOverride || params.teacherId;
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: '', start_time: '09:00', end_time: '17:00' });

  const fetchData = useCallback(async () => {
    try {
      const [teacherRes, winRes] = await Promise.all([
        axios.get(`${API}/teachers/${teacherId}`),
        axios.get(`${API}/teachers/${teacherId}/availability-dates`),
      ]);
      setTeacher(teacherRes.data);
      setWindows(winRes.data || []);
    } catch (error) {
      toast.error('Failed to load teacher data');
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!form.date || !form.start_time || !form.end_time) {
      toast.error('Please pick a date and start/end time');
      return;
    }
    if (form.end_time <= form.start_time) {
      toast.error('End time must be after start time');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/teachers/${teacherId}/availability-dates`, form);
      toast.success('Availability added — timeline generated automatically');
      setForm({ ...form, date: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add availability');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (windowId) => {
    try {
      await axios.delete(`${API}/teachers/${teacherId}/availability-dates/${windowId}`);
      toast.success('Availability removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove availability');
    }
  };

  const grouped = windows.reduce((acc, w) => {
    (acc[w.date] = acc[w.date] || []).push(w);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="availability-loading">
        <div className="text-lg text-slate-600">Loading availability...</div>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-slate-600">Teacher not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="availability-management">
      <div className="mb-8">
        {!teacherIdOverride && <Button
          variant="ghost"
          onClick={() => navigate('/admin/teachers')}
          className="mb-4 text-slate-600 hover:text-slate-900"
          data-testid="back-to-teachers"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Teachers
        </Button>}

        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-testid="availability-title">
          Manage Availability
        </h1>
        <p className="text-lg text-slate-600" data-testid="availability-subtitle">
          {teacherIdOverride ? 'Set' : `${teacher.name} — set`} the dates and working hours. The booking timeline is generated automatically in 30-minute steps.
        </p>
      </div>

      {/* Add availability window */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="border-0 shadow-xl mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-violet-600" />
              Add Available Date & Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1 w-full">
                <Label className="text-sm text-slate-600 mb-1 block">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="availability-date-input"
                />
              </div>
              <div className="w-full sm:w-40">
                <Label className="text-sm text-slate-600 mb-1 block">Start time</Label>
                <Input
                  type="time"
                  step="1800"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  data-testid="availability-start-input"
                />
              </div>
              <div className="w-full sm:w-40">
                <Label className="text-sm text-slate-600 mb-1 block">End time</Label>
                <Input
                  type="time"
                  step="1800"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  data-testid="availability-end-input"
                />
              </div>
              <Button
                onClick={handleAdd}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full w-full sm:w-auto"
                data-testid="add-availability-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                {saving ? 'Adding...' : 'Add'}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Tip: Add multiple windows for the same date (e.g. 09:00–12:00 and 15:00–18:00) to create a break in between.
            </p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Existing availability */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-600" />
              Scheduled Availability
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dates.length === 0 ? (
              <div className="text-center py-10 text-slate-500" data-testid="no-availability">
                No availability set yet. Add a date above to make this faculty bookable.
              </div>
            ) : (
              <div className="space-y-5">
                {dates.map((date) => (
                  <div key={date} data-testid={`availability-day-${date}`}>
                    <p className="font-semibold text-slate-900 mb-2">{fmtDate(date)}</p>
                    <div className="flex flex-wrap gap-2">
                      {grouped[date]
                        .sort((a, b) => a.start_time.localeCompare(b.start_time))
                        .map((w) => (
                          <div
                            key={w.id}
                            className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full pl-4 pr-2 py-1.5"
                            data-testid={`availability-window-${w.id}`}
                          >
                            <span className="text-sm font-medium text-emerald-800">
                              {w.start_time} – {w.end_time}
                            </span>
                            <Badge variant="outline" className="bg-white text-slate-500 text-xs">
                              {durationLabel(w.start_time, w.end_time)}
                            </Badge>
                            <button
                              onClick={() => handleDelete(w.id)}
                              className="text-rose-500 hover:bg-rose-100 rounded-full p-1"
                              data-testid={`delete-window-${w.id}`}
                              aria-label="Delete availability"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default TeacherAvailabilityManagement;
